import { isMapId, levelAt, mapInfo, teleportCommand } from "@69ly/maps";
import type { Stuff } from "@69ly/shared";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { CopyButton } from "../components/CopyButton.tsx";
import { CopyToMenu } from "../components/CopyTo.tsx";
import { Technique } from "../components/Technique.tsx";
import { MediaViewer } from "../components/StuffMedia.tsx";
import { BackLink } from "../components/BackLink.tsx";
import { OnMap, Radar, ThrowLine } from "../components/Radar.tsx";
import { StatusControl } from "../components/StatusControl.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { UsedInList } from "../components/UsedIn.tsx";
import { WindowsBar } from "../components/WindowsBar.tsx";
import { errorText } from "../lib/errors.ts";
import { useDeleteStuff, useStuff } from "../lib/stuff-queries.ts";
import { PlacesLine } from "./MapView.tsx";
import { ImportanceTag } from "../components/Importance.tsx";
import { GrenadeIcon, MarkerIcon } from "../components/MarkerIcon.tsx";

const fixed = (n: number, d = 2) => n.toFixed(d).replace(/\.?0+$/, "");

function StuffView({ stuff }: { stuff: Stuff }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remove = useDeleteStuff();
  const map = isMapId(stuff.map) ? stuff.map : null;
  const steps = [
    { key: "stand", help: t("detail.standHelp") },
    { key: "aim", help: t("detail.aimHelp") },
    { key: "throw", help: "" },
  ];
  return (
    <div className="page detail">
      {map && <BackLink fallback={`/library/${map}?s=${stuff.id}`} fallbackLabel={t("detail.back", { map: mapInfo(map).name })} />}
      <div className="page-head">
        <div className="detail-title">
          <GrenadeIcon grenade={stuff.grenade} side={stuff.side} size={44} />
          <div>
            <h1 className="page-title">{stuff.title}</h1>
            <p className="muted">
              <span className={`side-tag side-${stuff.side}`}>{t(`sideShort.${stuff.side}`)}</span> <ImportanceTag importance={stuff.importance} /> {t(`grenade.${stuff.grenade}`)} · {t(`area.${stuff.area}`)} ·{" "}
              {t("mapView.by", { name: stuff.author.name })}
            </p>
            <PlacesLine map={stuff.map} stuff={stuff} />
          </div>
        </div>
        <div className="row detail-actions">
          <CopyToMenu stuffId={stuff.id} from={stuff.teamId ?? "personal"} />
          {stuff.canEdit && (
            <Link to={`/stuffs/${stuff.id}/edit`} className="btn btn-secondary">
              {t("mapView.edit")}
            </Link>
          )}
          {stuff.canEdit && (
            <button
              type="button"
              className="btn btn-quiet btn-danger"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(t("detail.confirmDelete", { title: stuff.title })))
                  remove.mutate(stuff.id, { onSuccess: () => navigate(map ? `/library/${map}` : "/library", { replace: true }) });
              }}
            >
              {t("detail.delete")}
            </button>
          )}
        </div>
      </div>

      <div className="columns">
        <div className="column-main detail-main">
          <section className="card detail-media">
            <MediaViewer stuff={stuff} empty={t("detail.noPictures")} />
          </section>
          <ol className="steps-detail is-compact">
            {steps.map((s, i) => (
              <li key={s.key} className="card">
                <div className="step-head">
                  <span className="step-disc">{i + 1}</span>
                  <h2 className="section-title">{t(`detail.${s.key}`)}</h2>
                </div>
                {s.key === "throw" ? <Technique technique={stuff.technique} /> : <p>{s.help}</p>}
                {s.key === "stand" && (
                  <div className="command">
                    <code>{teleportCommand(stuff.throw)}</code>
                    <CopyButton text={teleportCommand(stuff.throw)} />
                  </div>
                )}
              </li>
            ))}
          </ol>
          {stuff.notes && (
            <section className="card">
              <h2 className="eyebrow">{t("detail.notes")}</h2>
              <p className="notes">{stuff.notes}</p>
            </section>
          )}
        </div>

        <aside className="column-side">
          {map && (
            <section className="card">
              <h2 className="eyebrow">{t("detail.map")}</h2>
              <div className="mini-radar">
                <Radar turnable map={map} mapName={mapInfo(map).name} level={levelAt(map, stuff.landing.z ?? stuff.throw.z)}>
                  <ThrowLine map={map} from={stuff.throw} to={stuff.landing} side={stuff.side} />
                  <OnMap map={map} x={stuff.throw.x} y={stuff.throw.y} className="throw-spot">
                    <span className={`throw-marker side-${stuff.side}`} title={t("mapView.throwFrom")}>
                      <MarkerIcon kind="launch" grenade={stuff.grenade} side={stuff.side} />
                    </span>
                  </OnMap>
                  <OnMap map={map} x={stuff.landing.x} y={stuff.landing.y}>
                    <span className={`marker is-static side-${stuff.side}`} title={t("mapView.landsHere")}>
                      <MarkerIcon kind="impact" grenade={stuff.grenade} side={stuff.side} />
                    </span>
                  </OnMap>
                </Radar>
              </div>
              <dl className="facts small">
                <dt>{t("detail.position")}</dt>
                <dd className="mono">
                  {fixed(stuff.throw.x)} {fixed(stuff.throw.y)} {fixed(stuff.throw.z)}
                </dd>
                <dt>{t("detail.angles")}</dt>
                <dd className="mono">
                  {fixed(stuff.throw.pitch, 3)} {fixed(stuff.throw.yaw, 3)}
                </dd>
              </dl>
            </section>
          )}
          <section className="card">
            <h2 className="eyebrow">{t("mapView.windows")}</h2>
            <WindowsBar windows={stuff.windows} />
          </section>
          <section className="card">
            <h2 className="eyebrow">{t("usedIn.title")}</h2>
            <UsedInList stuff={stuff} />
          </section>
          <section className="card">
            <h2 className="eyebrow">{t("mapView.myStatus")}</h2>
            <StatusControl stuffId={stuff.id} status={stuff.myStatus} />
            <p className="muted small">
              {stuff.teamId ? t("mapView.teamProgress", { learned: stuff.progress.learned, members: stuff.progress.members }) : t("mapView.personalProgress")}
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}

export function StuffDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const stuff = useStuff(id);
  if (stuff.isPending) return <Loading />;
  if (stuff.error) return <ErrorState message={errorText(t, stuff.error)} onRetry={() => void stuff.refetch()} />;
  return <StuffView stuff={stuff.data} />;
}
